// Turns per-unit invoices into the billing report's views: a monthly collection
// trend, the arrears list and an aging summary.
// Pure functions over getUnitBilling() output, so they're cheap to test.

import type { PublicInvoice } from '@residenone/shared';
import type { UnitBilling } from './ops-api';

export interface MonthTrend {
  month: string;
  collectedCents: number;
  outstandingCents: number;
  rate: number;
}

export interface ArrearsRow {
  unit_id: string;
  label: string;
  monthsBehind: number;
  outstandingCents: number;
  oldestMonth: string;
}

export interface Aging {
  upToOneMonth: number;
  twoToThreeMonths: number;
  overThreeMonths: number;
}

export interface BillingInsights {
  /** The months on the chart: the last 12, or one calendar year. */
  months: string[];
  /** Today's month. Aging and arrears are always measured from here. */
  currentMonth: string;
  /** Everything billed for today's month, used to express arrears in months of fees. */
  currentMonthBilledCents: number;
  trend: MonthTrend[];
  arrears: ArrearsRow[];
  aging: Aging;
}

const monthOf = (date: string) => date.slice(0, 7);
const isUnpaid = (i: PublicInvoice) => i.status === 'OPEN' || i.status === 'OVERDUE';
const owed = (i: PublicInvoice) => Math.max(0, i.total_cents - i.paid_cents);

/** The last `n` calendar months as 'YYYY-MM', oldest first, ending with today's month. */
export function lastMonths(today: Date, n: number): string[] {
  const out: string[] = [];
  for (let back = n - 1; back >= 0; back--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - back, 1));
    out.push(d.toISOString().slice(0, 7));
  }
  return out;
}

/** January to December of `year` as 'YYYY-MM'. */
export function monthsOfYear(year: number): string[] {
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
}

function monthsBetween(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number) as [number, number];
  const [ty, tm] = to.split('-').map(Number) as [number, number];
  return (ty - fy) * 12 + (tm - fm);
}

/**
 * `months` picks what the trend charts (default: the last `monthCount` months).
 * Arrears and aging ignore it and always describe today.
 */
export function buildBillingInsights(
  units: UnitBilling[],
  today: Date,
  monthCount = 12,
  months: string[] = lastMonths(today, monthCount),
): BillingInsights {
  const currentMonth = today.toISOString().slice(0, 7);

  const live = units.map((u) => ({ ...u, invoices: u.invoices.filter((i) => i.status !== 'VOID') }));

  const trend = months.map((month) => {
    let collectedCents = 0;
    let outstandingCents = 0;
    for (const u of live) {
      for (const i of u.invoices) {
        if (monthOf(i.issue_date) !== month) continue;
        if (i.status === 'PAID') collectedCents += i.total_cents;
        else if (isUnpaid(i)) outstandingCents += owed(i);
      }
    }
    const total = collectedCents + outstandingCents;
    return { month, collectedCents, outstandingCents, rate: total > 0 ? collectedCents / total : 0 };
  });

  const aging: Aging = { upToOneMonth: 0, twoToThreeMonths: 0, overThreeMonths: 0 };
  const arrears: ArrearsRow[] = [];
  for (const u of live) {
    const unpaid = u.invoices.filter(isUnpaid);
    if (unpaid.length === 0) continue;
    for (const i of unpaid) {
      const age = monthsBetween(monthOf(i.issue_date), currentMonth);
      if (age <= 1) aging.upToOneMonth += owed(i);
      else if (age <= 3) aging.twoToThreeMonths += owed(i);
      else aging.overThreeMonths += owed(i);
    }
    arrears.push({
      unit_id: u.unit_id,
      label: u.label,
      monthsBehind: unpaid.length,
      outstandingCents: unpaid.reduce((s, i) => s + owed(i), 0),
      oldestMonth: unpaid.map((i) => monthOf(i.issue_date)).sort()[0]!,
    });
  }
  arrears.sort((a, b) => b.monthsBehind - a.monthsBehind || b.outstandingCents - a.outstandingCents);

  let currentMonthBilledCents = 0;
  for (const u of live) {
    for (const i of u.invoices) {
      if (monthOf(i.issue_date) === currentMonth && (i.status === 'PAID' || isUnpaid(i))) currentMonthBilledCents += i.total_cents;
    }
  }

  return { months, currentMonth, currentMonthBilledCents, trend, arrears, aging };
}

export interface BillingStory {
  /** Mean collection rate over the charted months before today's (months with bills only). */
  avgRatePastMonths: number;
  pastMonthCount: number;
  monthsBelowTarget: number;
  housesBehind: number;
  housesBehindShare: number;
  owedCents: number;
  /** Total owed expressed as months of the cluster's whole monthly billing. */
  owedInMonthsOfFees: number;
}

/** The numbers behind the report's one-line takeaway. `totalHouses` includes houses with no bills. */
export function billingStory(insights: BillingInsights, totalHouses: number, targetRate: number): BillingStory {
  // Today's month is still being collected, so it would drag the average down.
  const past = insights.trend.filter((m) => m.month < insights.currentMonth && m.collectedCents + m.outstandingCents > 0);
  const monthlyBilled = insights.currentMonthBilledCents;
  const owedCents = insights.arrears.reduce((s, a) => s + a.outstandingCents, 0);
  return {
    avgRatePastMonths: past.length ? past.reduce((s, m) => s + m.rate, 0) / past.length : 0,
    pastMonthCount: past.length,
    monthsBelowTarget: past.filter((m) => m.rate < targetRate).length,
    housesBehind: insights.arrears.length,
    housesBehindShare: totalHouses > 0 ? insights.arrears.length / totalHouses : 0,
    owedCents,
    owedInMonthsOfFees: monthlyBilled > 0 ? owedCents / monthlyBilled : 0,
  };
}
